<script setup lang="ts">
  import { computed, onMounted, ref, toRaw, watch } from "vue";
  import {
    CURRENT_PUSH_NOTIFICATION_PROVIDER,
    registerServiceWorker,
    useSubscribePushNotification,
  } from "@/entities/pwa";
  import { PWA_EVENTS } from "@/entities/pwa/pwa.events";
  import { useRegister } from "@/hooks/use-register";

  const type = ref(CURRENT_PUSH_NOTIFICATION_PROVIDER);
  const force = true;
  const notificationAccess = PWA_EVENTS.NotificationAccess.useObserve();
  const canRequestNotificationAccess = computed(
    () => notificationAccess.value != "granted" && notificationAccess.value != "denied",
  );
  const workerInfo = PWA_EVENTS.WorkerInfo.useObserve();

  const { subscribe, canSubscribe } = useSubscribePushNotification({
    type,
    forceUnsubscribe: force,
    forceSubscribe: force,
    onCreateSubscriberError: (error) => {
      console.error("create push notification subscriber: ", error);
    },
    onSubscribeError: (error) => {
      console.error("subscribe to push notification: ", error);
    },
    onUnSubscribeError: (error) => {
      console.error("unsubscrtive from push notification: ", error);
    },
  });

  function emitNotificationAccess() {
    const access = window.Notification.permission;
    PWA_EVENTS.NotificationAccess.next(access);
  }

  async function observeNotificationAccess(signal: AbortSignal) {
    try {
      const accessStatus = await navigator.permissions.query({ name: "notifications" });
      if (!signal.aborted) {
        accessStatus.onchange = emitNotificationAccess;
      }
      return () => {
        accessStatus.onchange = null;
      };
    } catch (error) {
      console.error("register notifications access observer: ", error);
    }
    return () => {};
  }

  // observe notification access
  useRegister((signal) => {
    emitNotificationAccess();
    let clean: Callback | undefined;
    void observeNotificationAccess(signal).then((cb) => {
      clean = cb;
    });
    return () => {
      clean?.();
    };
  });

  // request notification access
  watch(
    canRequestNotificationAccess,
    (can) => {
      if (!can) return;
      void Notification.requestPermission();
    },
    { immediate: true },
  );

  // subscribe to notification
  watch(
    canSubscribe,
    (can) => {
      if (!can) return;
      void subscribe();
    },
    { immediate: true },
  );

  // worker info
  watch(
    workerInfo,
    (info) => {
      if (!info) return;
      // eslint-disable-next-line no-console
      console.log(toRaw(info));
    },
    { immediate: true },
  );

  // service worker
  onMounted(() => {
    registerServiceWorker();
  });
</script>

<template>
  <slot></slot>
</template>
